import { FrameRange } from 'content-creation-common/models';
import { isEmpty as isEmptyArray, sortBy } from './array';
import { isNotNumber } from './numbers';
import { isArray } from './object';

/**
 * Takes a list of frame ranges (sorts them), then moves all
 * frames from before the frame number to the end of the list,
 * splitting the frame range that contains the frame number.
 *
 * @param {FrameRange[]} frameRanges
 * @param {Number} frameNumber
 * @returns {FrameRange[]}
 */
export const shiftFrameRangesBeforeFrameToTail = (frameRanges, frameNumber) => {
  if (!isArray(frameRanges)) {
    throw new Error('frameRanges must be an array');
  }

  if (isNotNumber(frameNumber) || frameNumber < 0) {
    throw new Error('frameNumber must be a number >= 0');
  }

  if (isEmptyArray(frameRanges)) return [];

  if (!frameRanges.every((frameRange) => FrameRange.isFrameRange(frameRange))) {
    throw new Error('frameRanges must be valid FrameRange objects');
  }

  // Sort frame ranges by start frame in case they aren't
  frameRanges = sortBy(frameRanges, 'start');

  // If we're on the first frame, or asking for a frame out of range
  const firstRangeStart = frameRanges[0].start;
  const lastRangeEnd = frameRanges[frameRanges.length - 1].end;
  if (frameNumber <= firstRangeStart || frameNumber > lastRangeEnd) {
    return frameRanges;
  }

  // Move all ranges that are before to the end
  while (frameRanges[0].end < frameNumber) {
    frameRanges.push(frameRanges.shift());
  }

  if (frameRanges[0].start < frameNumber) {
    const firstRange = frameRanges.shift();
    frameRanges.unshift(new FrameRange(frameNumber, firstRange.end));
    frameRanges.push(new FrameRange(firstRange.start, frameNumber - 1));
  }
  return frameRanges;
};

/**
 * Takes a list of frame ranges and returns a list
 * of frame ranges that contains only "count" frames.
 *
 * @param {FrameRange[]} frameRanges
 * @param {Number} count
 * @returns {FrameRange[]}
 */
export const truncateFrameRanges = (frameRanges, count) => {
  if (!isArray(frameRanges)) {
    throw new Error('frameRanges must be an array');
  }

  if (isNotNumber(count) || count < 0) {
    throw new Error('count must be a number >= 0');
  }

  if (count === 0) return [];

  const collectedFrameRanges = [];

  // Not using reduce here to avoid going through all records
  for (const frameRange of frameRanges) {
    if (!FrameRange.isFrameRange(frameRange)) {
      throw new Error('frameRanges must be valid FrameRange objects');
    }
    const { length } = frameRange;
    if (length <= count) {
      collectedFrameRanges.push(frameRange);
      count -= length;
      if (count === 0) break;
    } else {
      collectedFrameRanges.push(
        new FrameRange(frameRange.start, frameRange.start + count - 1),
      );
      break;
    }
  }

  return collectedFrameRanges;
};
